// Section "Offrir 7 jours" et "Utiliser un code cadeau".
// À intégrer dans /premium ou /mon-espace.
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Gift, Copy, Check, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function GiftPanel() {
  const { user, refresh } = useAuth();
  const [my, setMy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [code, setCode] = useState("");
  const [redeem, setRedeem] = useState(false);
  const [copied, setCopied] = useState(false);

  async function load() {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await api.get("/gift/my");
      setMy(data);
    } catch (e) { console.warn("Chargement du cadeau échoué :", e?.message); }
    setLoading(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user]);

  async function generate() {
    if (gen) return;
    setGen(true);
    try {
      await api.post("/gift/generate");
      await load();
      toast.success("Code cadeau généré !");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible de générer un code.");
    }
    setGen(false);
  }

  async function activate() {
    const c = code.trim().toUpperCase();
    if (!c) return;
    if (redeem) return;
    setRedeem(true);
    try {
      const { data } = await api.post("/gift/redeem", { code: c });
      toast.success(`7 jours Premium débloqués jusqu'au ${new Date(data.ends_at).toLocaleDateString("fr-FR")}`);
      setCode("");
      await refresh();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Code invalide.");
    }
    setRedeem(false);
  }

  async function copyCode(c) {
    try {
      await navigator.clipboard.writeText(c);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible sur ce navigateur.");
    }
  }

  async function shareCode(c) {
    const text = `Je t'offre 7 jours de Soft Landing ASMR. Utilise le code ${c} depuis Mon espace pour l'activer. ${window.location.origin}/mon-espace`;
    if (navigator.share) {
      try { await navigator.share({ title: "Cadeau Soft Landing", text }); } catch (e) { if (e?.name !== "AbortError") toast.error("Partage impossible"); }
    } else {
      await copyCode(text);
      toast.success("Message copié");
    }
  }

  if (!user) return null;

  const activeGift = my?.active_gift;
  const activeGiftValid = activeGift && new Date(activeGift.ends_at) > new Date();
  const latestCode = my?.codes?.[0];

  return (
    <section className="mt-8" data-testid="gift-panel">
      <div className="flex items-center gap-2 mb-3">
        <Gift size={14} className="text-[color:var(--sauge-light)]" />
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">
          Cadeau Premium — 7 jours
        </p>
      </div>

      <div className="rounded-2xl border border-[color:var(--border)] bg-[#142235]/60 p-5 space-y-5">
        {/* Cadeau reçu actif */}
        {activeGiftValid && (
          <div className="rounded-xl bg-[color:var(--sauge)]/15 border border-[color:var(--sauge)]/40 p-4" data-testid="gift-active">
            <p className="flex items-center gap-2 text-sm text-[color:var(--sauge-light)] font-medium">
              <Sparkles size={14} /> Cadeau actif
            </p>
            <p className="text-xs text-[color:var(--ivoire-muted)] mt-1">
              Accès Premium jusqu'au {new Date(activeGift.ends_at).toLocaleDateString("fr-FR")}.
            </p>
          </div>
        )}

        {/* Générer / afficher son code */}
        <div>
          <p className="text-sm text-[color:var(--ivoire)] font-medium">Offrir 7 jours à un proche</p>
          <p className="text-xs text-[color:var(--ivoire-muted)] mt-1">
            Un code unique par mois, à partager par message.
          </p>
          {loading ? (
            <p className="text-xs text-[color:var(--ivoire-subtle)] mt-3">Chargement…</p>
          ) : latestCode ? (
            <div className="mt-3">
              <div className="flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-deep)]/50 p-3">
                <span
                  data-testid="my-gift-code"
                  className="flex-1 font-mono text-lg tracking-[0.2em] text-[color:var(--ivoire)] select-all"
                >
                  {latestCode.code}
                </span>
                <button
                  data-testid="copy-gift-code"
                  onClick={() => copyCode(latestCode.code)}
                  className="w-9 h-9 rounded-lg border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire-muted)] hover:text-[color:var(--ivoire)]"
                  aria-label="Copier"
                >
                  {copied ? <Check size={14} className="text-[color:var(--sauge-light)]" /> : <Copy size={14} />}
                </button>
                <button
                  data-testid="share-gift-code"
                  onClick={() => shareCode(latestCode.code)}
                  className="w-9 h-9 rounded-lg border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire-muted)] hover:text-[color:var(--ivoire)]"
                  aria-label="Partager"
                >
                  <Share2 size={14} />
                </button>
              </div>
              <p className="text-[11px] text-[color:var(--ivoire-subtle)] mt-2">
                {latestCode.redeemed
                  ? `Utilisé le ${new Date(latestCode.redeemed_at).toLocaleDateString("fr-FR")}.`
                  : "Non utilisé. Vous pourrez en générer un nouveau dans 30 jours."}
              </p>
            </div>
          ) : (
            <button
              data-testid="gen-gift-btn"
              onClick={generate}
              disabled={gen}
              className="mt-3 w-full h-12 rounded-xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium disabled:opacity-60"
            >
              {gen ? "…" : "Générer un code"}
            </button>
          )}
        </div>

        <div className="h-px bg-[color:var(--border)]" />

        {/* Utiliser un code reçu */}
        <div>
          <p className="text-sm text-[color:var(--ivoire)] font-medium">J'ai reçu un code</p>
          <p className="text-xs text-[color:var(--ivoire-muted)] mt-1">
            Saisissez-le pour activer 7 jours de Premium.
          </p>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="gift-redeem-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              maxLength={9}
              className="flex-1 h-12 px-4 rounded-xl bg-[#0F1B2E]/70 border border-[color:var(--border)] text-[color:var(--ivoire)] font-mono tracking-[0.2em]"
              autoCapitalize="characters"
              autoCorrect="off"
            />
            <button
              data-testid="gift-redeem-btn"
              onClick={activate}
              disabled={redeem || !code.trim()}
              className="h-12 px-5 rounded-xl border border-[color:var(--sauge)]/50 bg-[color:var(--sauge)]/15 text-[color:var(--sauge-light)] disabled:opacity-40"
            >
              {redeem ? "…" : "Activer"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
